import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import api from '../api/client';
import './RaceResultsViewer.css';

interface PenaltyDetail {
  id: string;
  resultFilename: string;
  driverId?: string;
  driverName: string;
  raceNumber?: string | number;
  seconds: number;
  reason: string;
  createdAt: number;
}

interface RaceStanding {
  position: number;
  positionChange?: number | null;
  driverId?: string | number;
  driverName: string;
  carModel: string;
  raceNumber: string | number;
  bestLapTime: number | null;
  totalTime: number | null;
  rawTotalTime: number | null;
  adjustedTotalTime: number | null;
  penaltySeconds: number;
  penalties: PenaltyDetail[];
  lapCount: number;
  gap: string | number;
}

interface RaceResultsViewerProps {
  serverId?: string;
  filename?: string;
  sessionId?: number;
  onClose: () => void;
  onDeleteSuccess?: () => void;
  fetchResult?: () => Promise<any>;
}

const RaceResultsViewer: React.FC<RaceResultsViewerProps> = ({ serverId, filename, sessionId, onClose, onDeleteSuccess, fetchResult }) => {
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    sessionType: string;
    trackName: string;
    serverName: string;
    isRaceSession: boolean;
    leaderLapCount: number;
    standings: RaceStanding[];
  } | null>(null);

  useEffect(() => {
    const loadResults = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = fetchResult
          ? await fetchResult()
          : await api.viewResult(serverId || '', filename || '');
        setData(res.data);
      } catch (err) {
        console.error('Failed to load race results:', err);
        setError('Failed to load race results');
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [serverId, filename, fetchResult]);

  const handleDeleteSession = async () => {
    const deleteDbConfirmed = window.confirm(
      'Delete this result session and all related rows from the database?'
    );
    if (!deleteDbConfirmed) {
      return;
    }

    const deleteFile = window.confirm(
      'Also delete the JSON file from disk so it will not be re-imported?'
    );

    try {
      setDeleting(true);
      setError(null);
      if (sessionId) {
        await api.deleteResultSession(sessionId.toString(), deleteFile);
      } else if (serverId && filename) {
        await api.deleteResultFile(serverId, filename, deleteFile);
      } else {
        setError('Unable to delete result; missing session or file reference.');
        return;
      }
      onDeleteSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete result session');
    } finally {
      setDeleting(false);
    }
  };

  const formatTime = (seconds: number | null): string => {
    if (seconds === null) return '-';
    
    const totalSeconds = Math.floor(seconds);
    const milliseconds = Math.round((seconds - totalSeconds) * 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    
    if (minutes > 0) {
      return `${minutes}:${String(secs).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
    }
    return `${secs}.${String(milliseconds).padStart(3, '0')}`;
  };

  if (loading) {
    return (
      <div className="race-results-modal-overlay">
        <div className="race-results-modal">
          <div className="modal-header">
            <h2>Loading Results...</h2>
            <button className="close-button" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="race-results-modal-overlay">
        <div className="race-results-modal">
          <div className="modal-header">
            <h2>Error</h2>
            <button className="close-button" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          <div className="error-message">{error || 'No data available'}</div>
        </div>
      </div>
    );
  }

  const renderGap = (standing: RaceStanding): string => {
    if (typeof standing.gap === 'string') {
      // For "+X laps" format
      if (standing.gap.includes('lap')) {
        return standing.gap;
      }
      // For timegap format
      return standing.gap;
    }
    return String(formatTime(standing.gap));
  };


  return (
    <div className="race-results-modal-overlay" onClick={onClose}>
      <div className="race-results-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>{data.sessionType} Results</h2>
            <div className="session-info">
              <span className="track-name">{data.trackName}</span>
              <span className="server-name">{data.serverName}</span>
              {data.isRaceSession && (
                <span className="lap-info">{data.leaderLapCount} laps</span>
              )}
            </div>
          </div>
          <div className="modal-actions">
            {(sessionId !== undefined || (serverId && filename)) && (
              <button className="delete-button" onClick={handleDeleteSession} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <button className="close-button" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
        </div>

        {(data.standings?.some((standing) => standing.penalties?.length > 0) ?? false) && (
          <div className="penalty-summary-bar">
            <strong>Penalties applied:</strong> {(data.standings ?? []).reduce((count, standing) => count + (standing.penalties?.length ?? 0), 0)} total
          </div>
        )}

        <div className="results-table-container">
          <table className="results-standings-table">
            <thead>
              <tr>
                <th className="col-position">Pos</th>
                <th className="col-driver">Driver</th>
                <th className="col-car">Car</th>
                <th className="col-number">#</th>
                <th className="col-best">Best Lap</th>
                {data.isRaceSession && (<th className="col-time">Total Time</th>)}
                {data.isRaceSession && (<th className="col-penalty">Penalty</th>)}
                {data.isRaceSession && (<th className="col-laps">Laps</th>)}
                <th className="col-gap">Gap</th>
              </tr>
            </thead>
            <tbody>
              {(data.standings ?? []).map((standing) => (
                <tr key={standing.position} className={`standing-row ${standing.position === 1 ? 'winner' : ''}`}>
                  <td className="col-position">
                    <span className="position-badge">{standing.position}</span>
                  </td>
                  <td className="col-driver">
                    <span className="driver-name">{standing.driverName}</span>
                  </td>
                  <td className="col-car">
                    <span className="car-model">{standing.carModel}</span>
                  </td>
                  <td className="col-number">
                    <span className="race-number">{standing.raceNumber}</span>
                  </td>
                  <td className="col-best">
                    <span className="lap-best">{formatTime(standing.bestLapTime)}</span>
                  </td>
                  {data.isRaceSession && (
                    <td className="col-time">
                      <span className="lap-time">{formatTime(standing.totalTime)}</span>
                    </td>
                  )}
                {data.isRaceSession && (
                  <td className="col-penalty">
                    {standing.penalties?.length > 0 ? (
                      <span className="penalty-cell">
                        <span className="penalty-value">+{Math.round(standing.penaltySeconds ?? 0)}s</span>
                        <span className="penalty-info" tabIndex={0} aria-label="Penalty reason">
                          <Info size={14} />
                          <span className="penalty-tooltip">
                            {(standing.penalties ?? []).map((penalty) => penalty.reason).join('\n')}
                          </span>
                        </span>
                      </span>
                    ) : (
                      <span className="no-penalty">—</span>
                    )}
                  </td>
                    )}
                {data.isRaceSession && (
                  <td className="col-laps">
                    <span className="lap-count">{standing.lapCount}</span>
                  </td>
                )}
                <td className="col-gap">
                  <span className={`gap ${standing.position === 1 ? 'leader' : ''}`}>
                    {renderGap(standing)}
                  </span>
                </td>
              </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RaceResultsViewer;
